import { AreaChart, Area, LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ComposedChart, Cell } from "recharts";
import { fmtPeriod } from "@/lib/format";

const COLORS = ["#0A2A22", "#2B4C7E", "#C05E4C", "#D4A373", "#575F5A", "#8A938D"];

const TooltipStyle = {
    backgroundColor: "#FFFFFF",
    border: "1px solid #E8E6E1",
    borderRadius: 0,
    padding: "10px 14px",
    fontFamily: "IBM Plex Sans",
    fontSize: 12,
    boxShadow: "none",
};

const axisProps = { stroke: "#8A938D", tick: { fontSize: 11, fontFamily: "JetBrains Mono", fill: "#575F5A" }, tickLine: false, axisLine: { stroke: "#E8E6E1" } };

export const RevenueVsBudgetChart = ({ periods, revenue, budget, priorYear }) => {
    const data = periods.map((p, i) => ({
        period: fmtPeriod(p).slice(0, 3) + "'" + p.slice(2, 4),
        Actual: revenue[i],
        Budget: budget[i],
        "Prior Year": priorYear[i],
    }));
    return (
        <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E8E6E1" vertical={false} />
                    <XAxis dataKey="period" {...axisProps} />
                    <YAxis {...axisProps} tickFormatter={(v) => `$${v}M`} />
                    <Tooltip contentStyle={TooltipStyle} formatter={(v) => `$${v.toFixed(1)}M`} />
                    <Legend wrapperStyle={{ fontFamily: "IBM Plex Sans", fontSize: 12, paddingTop: 8 }} iconType="circle" />
                    <Bar dataKey="Actual" fill="#0A2A22" barSize={18} />
                    <Line type="monotone" dataKey="Budget" stroke="#C05E4C" strokeWidth={2} dot={false} strokeDasharray="4 4" />
                    <Line type="monotone" dataKey="Prior Year" stroke="#8A938D" strokeWidth={1.5} dot={false} />
                </ComposedChart>
            </ResponsiveContainer>
        </div>
    );
};

export const EbitdaMarginChart = ({ periods, ebitda, marginPct }) => {
    const data = periods.map((p, i) => ({
        period: fmtPeriod(p).slice(0, 3) + "'" + p.slice(2, 4),
        EBITDA: ebitda[i],
        Margin: marginPct[i],
    }));
    return (
        <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E8E6E1" vertical={false} />
                    <XAxis dataKey="period" {...axisProps} />
                    <YAxis yAxisId="left" {...axisProps} tickFormatter={(v) => `$${v}M`} />
                    <YAxis yAxisId="right" orientation="right" {...axisProps} tickFormatter={(v) => `${v}%`} />
                    <Tooltip contentStyle={TooltipStyle} />
                    <Legend wrapperStyle={{ fontFamily: "IBM Plex Sans", fontSize: 12, paddingTop: 8 }} iconType="circle" />
                    <Bar yAxisId="left" dataKey="EBITDA" fill="#2B4C7E" barSize={18} />
                    <Line yAxisId="right" type="monotone" dataKey="Margin" stroke="#C05E4C" strokeWidth={2} dot={{ fill: "#C05E4C", r: 3 }} />
                </ComposedChart>
            </ResponsiveContainer>
        </div>
    );
};

export const SectorRevenueChart = ({ sectors }) => {
    const data = sectors.map((s) => ({ name: s.sector_name.split(" ")[0], value: s.revenue_musd, ebitdaMargin: s.ebitda_margin_pct }));
    return (
        <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E8E6E1" vertical={false} />
                    <XAxis dataKey="name" {...axisProps} />
                    <YAxis {...axisProps} tickFormatter={(v) => `$${v}M`} />
                    <Tooltip contentStyle={TooltipStyle} formatter={(v, n) => n === "value" ? [`$${v.toFixed(1)}M`, "Revenue"] : [`${v}%`, "Margin"]} />
                    <Bar dataKey="value" radius={0}>
                        {data.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Bar>
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
};

export const CashFlowChart = ({ periods, ocf, capex, fcf }) => {
    const data = periods.map((p, i) => ({
        period: fmtPeriod(p).slice(0, 3),
        "Op. Cash Flow": ocf[i],
        "CapEx": -capex[i],
        "Free Cash Flow": fcf[i],
    }));
    return (
        <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }} stackOffset="sign">
                    <CartesianGrid strokeDasharray="3 3" stroke="#E8E6E1" vertical={false} />
                    <XAxis dataKey="period" {...axisProps} />
                    <YAxis {...axisProps} tickFormatter={(v) => `$${v}M`} />
                    <Tooltip contentStyle={TooltipStyle} formatter={(v) => `$${v.toFixed(1)}M`} />
                    <Legend wrapperStyle={{ fontFamily: "IBM Plex Sans", fontSize: 12, paddingTop: 8 }} iconType="circle" />
                    <Bar dataKey="Op. Cash Flow" stackId="a" fill="#2D6A4F" />
                    <Bar dataKey="CapEx" stackId="a" fill="#C05E4C" />
                    <Line type="monotone" dataKey="Free Cash Flow" stroke="#0A2A22" strokeWidth={2} dot={{ r: 3, fill: "#0A2A22" }} />
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
};

export const ProjectionChart = ({ projection, baseline }) => {
    const data = projection.map((p) => ({
        month: `M${p.month_index}`,
        Revenue: p.revenue_musd,
        EBITDA: p.ebitda_musd,
        FCF: p.free_cash_flow_musd,
    }));
    return (
        <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                    <defs>
                        <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#0A2A22" stopOpacity={0.25} />
                            <stop offset="100%" stopColor="#0A2A22" stopOpacity={0} />
                        </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E8E6E1" vertical={false} />
                    <XAxis dataKey="month" {...axisProps} />
                    <YAxis {...axisProps} tickFormatter={(v) => `$${v}M`} />
                    <Tooltip contentStyle={TooltipStyle} formatter={(v) => `$${v.toFixed(1)}M`} />
                    <Legend wrapperStyle={{ fontFamily: "IBM Plex Sans", fontSize: 12, paddingTop: 8 }} iconType="circle" />
                    <Area type="monotone" dataKey="Revenue" stroke="#0A2A22" fill="url(#revGrad)" strokeWidth={2} />
                    <Line type="monotone" dataKey="EBITDA" stroke="#2B4C7E" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="FCF" stroke="#C05E4C" strokeWidth={2} dot={false} />
                </AreaChart>
            </ResponsiveContainer>
        </div>
    );
};

// ============= Differentiated Hero Charts (one per page context) =============

// GROUP OVERVIEW hero: Revenue Composition Waterfall — shows PY → +Growth → +Volume → +Price → Actual
export const RevenueBridgeChart = ({ revenue, priorYear, budget }) => {
    const last = revenue.length - 1;
    const py = priorYear[last];
    const actual = revenue[last];
    const budgetV = budget[last];
    const volumeContribution = (actual - py) * 0.6;
    const priceContribution = (actual - py) * 0.3;
    const fxContribution = (actual - py) - volumeContribution - priceContribution;
    const missBudget = actual - budgetV;

    const data = [
        { name: "Prior Year", value: py, fill: "#8A938D", stack: py },
        { name: "+ Volume", value: volumeContribution, fill: "#2D6A4F", stack: py + Math.max(0, volumeContribution) },
        { name: "+ Price/Mix", value: priceContribution, fill: "#1F5F5B", stack: py + volumeContribution + Math.max(0, priceContribution) },
        { name: "± FX", value: fxContribution, fill: fxContribution >= 0 ? "#3B7A73" : "#C05E4C", stack: py + volumeContribution + priceContribution + Math.max(0, fxContribution) },
        { name: "Actual", value: actual, fill: "#0A2A22", stack: actual },
        { name: "vs Budget", value: missBudget, fill: missBudget >= 0 ? "#2D6A4F" : "#C05E4C", stack: budgetV + Math.max(0, missBudget) },
    ];
    return (
        <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data} margin={{ top: 20, right: 10, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E8E6E1" vertical={false} />
                    <XAxis dataKey="name" {...axisProps} />
                    <YAxis {...axisProps} tickFormatter={(v) => `$${v}M`} />
                    <Tooltip contentStyle={TooltipStyle} formatter={(v) => `$${v.toFixed(1)}M`} />
                    <Bar dataKey="value" barSize={44} radius={0}>
                        {data.map((d, i) => <Cell key={i} fill={d.fill} />)}
                    </Bar>
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
};

// SECTOR page hero: Benchmark Positioning — sector performance vs industry P50/P75
export const SectorBenchmarkChart = ({ sectorName, revenue, ebitdaMargin, marketRevenueP50 = 100, marketRevenueP75 = 200, marketMarginP50 = 12, marketMarginP75 = 18 }) => {
    const lastRev = revenue[revenue.length - 1];
    const lastMargin = ebitdaMargin[ebitdaMargin.length - 1];
    // Bubble-like grouping vs benchmark
    const data = [
        { name: "P50 Peer", revenue: marketRevenueP50, margin: marketMarginP50, fill: "#D4A373" },
        { name: sectorName, revenue: lastRev, margin: lastMargin, fill: "#0A2A22" },
        { name: "P75 Peer", revenue: marketRevenueP75, margin: marketMarginP75, fill: "#3B7A73" },
    ];
    return (
        <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={data} margin={{ top: 20, right: 20, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E8E6E1" vertical={false} />
                    <XAxis dataKey="name" {...axisProps} />
                    <YAxis yAxisId="left" {...axisProps} tickFormatter={(v) => `$${v}M`} />
                    <YAxis yAxisId="right" orientation="right" {...axisProps} tickFormatter={(v) => `${v}%`} />
                    <Tooltip contentStyle={TooltipStyle} />
                    <Legend wrapperStyle={{ fontFamily: "IBM Plex Sans", fontSize: 12, paddingTop: 8 }} iconType="circle" />
                    <Bar yAxisId="left" dataKey="revenue" name="Revenue" barSize={54}>
                        {data.map((d, i) => <Cell key={i} fill={d.fill} />)}
                    </Bar>
                    <Line yAxisId="right" type="monotone" dataKey="margin" name="EBITDA Margin %" stroke="#C05E4C" strokeWidth={3} dot={{ fill: "#C05E4C", r: 6 }} />
                </ComposedChart>
            </ResponsiveContainer>
        </div>
    );
};

// COUNTRY page hero: Sector-mix stacked area — how the country's revenue is composed by sectors over time
export const CountrySectorMixChart = ({ periods, sectors }) => {
    // sectors: [{name, monthly:[...]}]
    const data = periods.map((p, i) => {
        const row = { period: p.slice(5) };
        sectors.forEach((s) => { row[s.name] = s.monthly[i]; });
        return row;
    });
    const colors = ["#0A2A22", "#2B4C7E", "#3B7A73", "#7B3F5C", "#D4A373", "#C05E4C", "#E19825"];
    return (
        <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E8E6E1" vertical={false} />
                    <XAxis dataKey="period" {...axisProps} />
                    <YAxis {...axisProps} tickFormatter={(v) => `$${v}M`} />
                    <Tooltip contentStyle={TooltipStyle} formatter={(v) => `$${v.toFixed(1)}M`} />
                    <Legend wrapperStyle={{ fontFamily: "IBM Plex Sans", fontSize: 11, paddingTop: 8 }} iconType="square" />
                    {sectors.map((s, i) => (
                        <Area key={s.name} type="monotone" dataKey={s.name} stackId="1" stroke={colors[i % colors.length]} fill={colors[i % colors.length]} fillOpacity={0.75} />
                    ))}
                </AreaChart>
            </ResponsiveContainer>
        </div>
    );
};

